import type { CompileContext } from "../compile/context.ts";
import type { EditorCatalogue } from "./catalogue.ts";
import { grownCatalogue, grownContext, type LibraryGrowth } from "./late-frames.ts";

/**
 * The store's catalogue and compile context as a source it reads through (ADR-130 decision 2.8, ADR-129 decision
 * 3.7 (a)). Each growth is a new immutable value holding the new entries, and subscribers are told; a value
 * handed out earlier is never changed by a later growth. The context a source hands out is still the object the
 * editor's own footprint and depth edits write into, so a growth carries those edits over by copying what the
 * current context holds.
 */

/**
 * What a source holds at one moment.
 */
export interface LibraryValue {
  readonly catalogue: EditorCatalogue;
  readonly context: CompileContext;
}

/**
 * Derives the parts of a context that follow from its terrains and the catalogue's tilesets, keeping the rest.
 */
export type ContextCompletion = (value: LibraryValue) => CompileContext;

/**
 * Told, after each growth, the value that now stands.
 */
export type LibraryListener = (value: LibraryValue) => void;

/**
 * The number of entries a catalogue and a context hold, to tell whether a growth added any.
 *
 * @param value The value.
 * @returns A count that rises when anything was added.
 */
function weight(value: LibraryValue): number {
  const { catalogue, context } = value;
  const keys = (record: object | undefined): number => (record === undefined ? 0 : Object.keys(record).length);
  return catalogue.props.length + catalogue.tilesets.length + (catalogue.packs?.length ?? 0) + keys(context.collision.footprints) + keys(context.frameSizes) + keys(context.derivedFootprints) + keys(context.savedDepths) + keys(context.standMarks) + keys(context.transitionStrips) + context.terrains.length + context.ruleSets.length - (catalogue.lostPulls?.length ?? 0);
}

/**
 * A stage of the background load of the world's other terrains and tilesets: the terrains the world's tilesets bring, and
 * the tilesets that hold tiles and no terrain.
 */
export type RestStage = "terrains" | "tilesets";

/**
 * A source of the catalogue and the context, that grows.
 */
export class LibrarySource {
  private value: LibraryValue;
  private readonly listeners = new Set<LibraryListener>();
  private live = false;
  private given = new Set<string>();
  private arrivingTerrains = false;
  private readonly wantedStages = new Set<RestStage>();
  private readonly wantCounts = new Map<RestStage, number>();
  private idler: (() => void) | null = null;
  private art = 0;
  private readonly inHand = new Map<string, readonly string[]>();
  private loader: ((stage: RestStage) => void) | null = null;
  private provider: ((ids: readonly string[]) => Promise<void>) | null = null;

  /**
   * @param catalogue The catalogue the editor opened with.
   * @param context The compile context the editor opened with.
   * @param complete Derives the parts of the context that depend on its terrains and the catalogue's tilesets (the
   *   game's collision rules and surfaces), applied once to the value given and again after each growth that changes
   *   either; none leaves the context as it is grown.
   */
  public constructor(
    catalogue: EditorCatalogue,
    context: CompileContext,
    private readonly complete?: ContextCompletion,
  ) {
    this.value = complete === undefined ? { catalogue, context } : { catalogue, context: complete({ catalogue, context }) };
  }

  /**
   * The value that stands now.
   *
   * @returns The catalogue and the context; a later growth does not change what this returns, it replaces it.
   */
  public current(): LibraryValue {
    return this.value;
  }

  /**
   * The catalogue that stands now.
   *
   * @returns The catalogue.
   */
  public get catalogue(): EditorCatalogue {
    return this.value.catalogue;
  }

  /**
   * The context that stands now.
   *
   * @returns The context.
   */
  public get context(): CompileContext {
    return this.value.context;
  }

  /**
   * Whether this tab fetches picked frames as they are pulled: until it does, a pull waits for a compile.
   *
   * @returns True once `arrive` was called.
   */
  public get arriving(): boolean {
    return this.live;
  }

  /**
   * The frame ids live arrival gave up on: asked for and not answered, or not held by the pack. A pull of one
   * still waits for a compile.
   *
   * @returns The ids, in the order they were given up.
   */
  public get gaveUp(): ReadonlySet<string> {
    return this.given;
  }

  /**
   * Says a palette that needs a stage of the background load is shown (P1.6-T286): the loader is told the first time a
   * stage is wanted, and never for a stage nobody shows, so the art of a palette nobody opens is never brought.
   *
   * @param stage The stage.
   * @returns What gives the want up, which a palette calls when it is hidden; the owner is told when no palette wants any
   *   stage (`onUnwanted`).
   */
  public want(stage: RestStage): () => void {
    this.wantCounts.set(stage, (this.wantCounts.get(stage) ?? 0) + 1);
    if (!this.wantedStages.has(stage)) {
      this.wantedStages.add(stage);
      this.loader?.(stage);
    }
    let given = false;
    return () => {
      if (given) {
        return;
      }
      given = true;
      this.wantCounts.set(stage, (this.wantCounts.get(stage) ?? 1) - 1);
      if ([...this.wantCounts.values()].every((count) => count <= 0)) {
        this.idler?.();
      }
    };
  }

  /**
   * Whether a palette wants a stage now: the owner reads it between batches, and stops a stage that nobody shows.
   *
   * @param stage The stage.
   * @returns True while a want of the stage has not been given up.
   */
  public holds(stage: RestStage): boolean {
    return (this.wantCounts.get(stage) ?? 0) > 0;
  }

  /**
   * Sets what brings a stage when a palette first wants it, and tells it at once of the stages already wanted.
   *
   * @param loader Brings a stage.
   */
  public onWanted(loader: (stage: RestStage) => void): void {
    this.loader = loader;
    for (const stage of this.wantedStages) {
      loader(stage);
    }
  }

  /**
   * Sets what is told when the last palette that wanted a stage lets go of it.
   *
   * @param idler Told once each time no palette wants any stage.
   */
  public onUnwanted(idler: () => void): void {
    this.idler = idler;
  }

  /**
   * Whether any palette wants a stage now.
   *
   * @returns True while a want has not been given up.
   */
  public get holding(): boolean {
    return [...this.wantCounts.values()].some((count) => count > 0);
  }

  /**
   * How many times the art of the palettes was brought again after it was let go. A palette that keys its rows on it
   * draws them again from the pages that arrived.
   *
   * @returns The count.
   */
  public get artRevision(): number {
    return this.art;
  }

  /**
   * Says the pages the palettes draw from changed with no growth of the library: subscribers are told and the
   * revision moves.
   */
  public refresh(): void {
    this.art += 1;
    this.tell();
  }

  /**
   * Forgets the stages that were brought, as the owner does when it has let the art of the palettes go or stopped a
   * stage nobody shows, so the next palette that is shown asks for them again. A stage some palette wants now is asked
   * for again at once.
   *
   * @param stage The stage to forget; every stage when absent.
   */
  public forget(stage?: RestStage): void {
    const forgotten = stage === undefined ? [...this.wantedStages] : [stage];
    for (const each of forgotten) {
      this.wantedStages.delete(each);
    }
    for (const each of forgotten) {
      if (this.holds(each)) {
        this.wantedStages.add(each);
        this.loader?.(each);
      }
    }
  }

  /**
   * Says which frames a panel holds in the hand, which the owner keeps in the pages it draws from when it lets the
   * palettes' art go.
   *
   * @param owner The panel's name; its frames replace what it said before.
   * @param frames The frame ids.
   */
  public keepInHand(owner: string, frames: readonly string[]): void {
    if (frames.length === 0) {
      this.inHand.delete(owner);
    } else {
      this.inHand.set(owner, frames);
    }
  }

  /**
   * The frames the panels hold in the hand.
   *
   * @returns The frame ids.
   */
  public get kept(): string[] {
    return [...this.inHand.values()].flat();
  }

  /**
   * Whether the terrains of the tilesets the world lists whole are still on their way, so a palette can say that its
   * list is not yet complete.
   *
   * @returns True between `awaitTerrains(true)` and `awaitTerrains(false)`.
   */
  public get terrainsArriving(): boolean {
    return this.arrivingTerrains;
  }

  /**
   * Records that the world's other terrains are on their way, or that they have all arrived or given up. Subscribers
   * are told when the state changes.
   *
   * @param on Whether terrains are still arriving.
   */
  public awaitTerrains(on: boolean): void {
    if (this.arrivingTerrains !== on) {
      this.arrivingTerrains = on;
      this.tell();
    }
  }

  /**
   * Sets how this tab brings frames by id: the page hands the function that asks the frames service, stamps what comes
   * and grows the library.
   *
   * @param provider Brings the frames of the ids.
   */
  public provide(provider: (ids: readonly string[]) => Promise<void>): void {
    this.provider = provider;
  }

  /**
   * Asks for the frames of ids the tab does not hold, as an item taken in hand from a tileset the world lists whole
   * needs them (ADR-130 decision 3.1). The answer arrives as a growth of the library.
   *
   * @param ids Frame ids.
   * @returns A promise that settles when the frames are in or the request has failed, or null when the tab has no way
   *   to bring them.
   */
  public request(ids: readonly string[]): Promise<void> | null {
    if (this.provider === null) {
      return null;
    }
    this.forgive(ids);
    return this.provider(ids);
  }

  /**
   * Marks that this tab fetches picked frames as they are pulled. Subscribers are told once.
   */
  public arrive(): void {
    if (!this.live) {
      this.live = true;
      this.tell();
    }
  }

  /**
   * Records frames that live arrival could not bring, which a pull then shows as waiting for a compile. A
   * subscriber is told when the set grew.
   *
   * @param frames Frame ids.
   */
  public giveUp(frames: readonly string[]): void {
    const next = new Set(this.given);
    for (const frame of frames) {
      next.add(frame);
    }
    if (next.size !== this.given.size) {
      this.given = next;
      this.tell();
    }
  }

  /**
   * Takes back frames a later attempt brought, so a pull no longer shows as waiting.
   *
   * @param frames Frame ids.
   */
  public forgive(frames: readonly string[]): void {
    if (frames.some((frame) => this.given.has(frame))) {
      const next = new Set(this.given);
      for (const frame of frames) {
        next.delete(frame);
      }
      this.given = next;
      this.tell();
    }
  }

  /**
   * Grows the catalogue and the context by what arrived.
   *
   * @param growth The arrived entries.
   * @returns True when anything was added, and subscribers were told; false when everything was held already.
   */
  public grow(growth: LibraryGrowth): boolean {
    const grown: LibraryValue = { catalogue: grownCatalogue(this.value.catalogue, growth), context: grownContext(this.value.context, growth) };
    const changed = grown.context.terrains !== this.value.context.terrains || grown.catalogue.tilesets.length !== this.value.catalogue.tilesets.length;
    const next: LibraryValue = this.complete === undefined || !changed ? grown : { catalogue: grown.catalogue, context: this.complete(grown) };
    if (weight(next) === weight(this.value)) {
      return false;
    }
    this.value = next;
    this.tell();
    return true;
  }

  /**
   * Asks to be told after each growth.
   *
   * @param listener Called with the new value.
   * @returns Stops the telling.
   */
  public subscribe(listener: LibraryListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Tells every subscriber the value that stands.
   */
  private tell(): void {
    for (const listener of [...this.listeners]) {
      listener(this.value);
    }
  }
}
